import React from "react";
import { View, Text, TouchableOpacity, Modal, Image } from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppImages } from "../../utility/AppImages";

interface GreatEffortModalProps {
  visible: boolean;
  onClose: () => void;
  onExploreMoreEvents: () => void;
}

export default function GreatEffortModal({
  visible,
  onClose,
  onExploreMoreEvents,
}: GreatEffortModalProps) {
  const deviceSize = useDeviceSize();
  const styles = useStyles();

  return (
    <Modal transparent visible={visible} animationType="fade">
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Header with Close Button */}
          <View style={styles.headerContainer}>
            <Text style={styles.headerText}>You Gave It Your All!</Text>
            <TouchableOpacity onPress={onClose}>
              <View
                style={{
                  padding: 7,
                  borderWidth: 1,
                  borderRadius: 6,
                  borderColor: AppColors.grayColor_393939,
                }}
              >
                <Image source={AppImages.icCancel} style={styles.closeIcon} />
              </View>
            </TouchableOpacity>
          </View>
          <View style={styles.separator} />

          <View style={styles.innerContainer}>
            <Text style={styles.mainHeading}>Great effort!</Text>
            <Text style={styles.description}>
              Every loss is a step closer to victory.{"\n"}
              Learn from this match and come back stronger. Keep playing, keep
              improving, and your moment to shine will come!
            </Text>

            {/* Explore More Events Button */}
            <TouchableOpacity
              style={styles.exploreButton}
              onPress={onExploreMoreEvents}
            >
              <Text style={styles.exploreButtonText}>Explore more Events</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/* 🖌 Responsive Styling */
const useStyles = CreateResponsiveStyle(
  {
    overlay: {
      flex: 1,
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      justifyContent: "center",
      alignItems: "center",
    },
    modalContainer: {
      backgroundColor: AppColors.black_121212,
      borderRadius: 20,
      width: "50%",
      paddingVertical: 25,
      paddingHorizontal: 25,
    },
    headerContainer: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 15,
    },
    headerText: {
      ...AppFontStyle.BOLD_ANYBODY_24,
      color: AppColors.white,
    },
    closeIcon: {
      width: 18,
      height: 18,
      tintColor: AppColors.white,
      resizeMode: "contain",
    },
    innerContainer: {
      backgroundColor: AppColors.purple_453B50,
      padding: 24,
      borderRadius: 20,
      alignItems: "center",
      justifyContent: "center",
    },
    mainHeading: {
      ...AppFontStyle.BOLD_ANYBODY_24,
      color: AppColors.white,
      marginBottom: 15,
    },
    description: {
      ...AppFontStyle.REGULAR_ANYBODY_16,
      color: AppColors.white,
      textAlign: "center",
      lineHeight: 22,
      marginBottom: 40,
    },
    exploreButton: {
      backgroundColor: AppColors.appViolentColor_6D08C3,
      paddingVertical: 12,
      paddingHorizontal: 20,
      borderRadius: 8,
    },
    exploreButtonText: {
      color: AppColors.white,
      fontSize: 16,
      fontWeight: "bold",
    },
    separator: {
      backgroundColor: AppColors.grayColor_282828,
      height: 1,
      width: "97%",
      marginBottom: 15,
      marginHorizontal: 10,
    },
  },
  {
    [DEVICE_SIZES.XS]: {
      modalContainer: {
        width: "95%",
      },
      headerText: {
        fontSize: 18,
      },
      innerContainer: {
        marginHorizontal: 0,
      },

      mainHeading: {
        fontSize: 18,
      },
      description: {
        fontSize: 14,
        lineHeight: 18,
        paddingHorizontal: 10,
      },
      exploreButton: {
        paddingVertical: 10,
        paddingHorizontal: 16,
      },
      exploreButtonText: {
        fontSize: 14,
      },
    },
    [DEVICE_SIZES.SM]: {
      modalContainer: {
        width: "85%",
      },
      innerContainer: {
        marginHorizontal: 0,
      },
      mainHeading: {
        fontSize: 20,
      },
      description: {
        fontSize: 15,
        lineHeight: 20,
        paddingHorizontal: 15,
      },
      exploreButton: {
        paddingVertical: 10,
        paddingHorizontal: 18,
      },
    },
    [DEVICE_SIZES.MD]: {
      modalContainer: {
        width: "65%",
      },
      innerContainer: {
        marginHorizontal: 0,
      },
      mainHeading: {
        fontSize: 22,
      },
      description: {
        fontSize: 16,
        lineHeight: 22,
        paddingHorizontal: 15,
      },
      exploreButton: {
        paddingVertical: 12,
        paddingHorizontal: 20,
      },
    },
    [DEVICE_SIZES.LG]: {
      modalContainer: {
        width: "50%",
      },
      innerContainer: {
        marginHorizontal: 0,
      },
    },
  }
);
